import React, { useEffect, useState } from "react";

const SECTIONS = [
  {
    id: "big-picture",
    title: "AI kya hai? (Big Picture)",
    blocks: [
      { type: "p", text: "AI ka matlab hai aisi machine banana jo insaan jaisa \"sochne\" wala kaam kar sake: samajhna, seekhna, decide karna. Ye ek bada umbrella hai, aur uske andar chhote-chhote circles hote hain." },
      { type: "ol", items: [
        "**AI (Artificial Intelligence):** sabse bada concept. Koi bhi system jo smart kaam kare, jaise chess khelna ya spam pehchanna.",
        "**ML (Machine Learning):** AI ka hissa. Hum rules likhne ke bajay machine ko *examples* dikhate hain, aur wo khud pattern seekhti hai.",
        "**Deep Learning:** ML ka hissa jo *neural networks* use karta hai. Image, voice aur language mein kamaal karta hai.",
        "**Generative AI (GenAI):** Deep Learning ka hissa jo *naya content* banata hai: text, image, code, music. ChatGPT aur Claude isi category mein aate hain.",
      ] },
      { type: "tip", label: "Ek line mein", text: "AI ⊃ ML ⊃ Deep Learning ⊃ GenAI. Har agla term pichhle ka specialized form hai." },
      { type: "tip", label: "Daily-life example", text: "Gmail ka spam filter ML hai. Face unlock Deep Learning hai. ChatGPT jaisa chatbot GenAI hai." },
    ],
  },
  {
    id: "ml-fundamentals",
    title: "1. ML Fundamentals",
    blocks: [
      { type: "p", text: "Normal programming mein hum *rules* likhte hain: \"agar X ho toh Y karo\". ML mein ulta hota hai: hum *data* dete hain aur machine khud rules nikalti hai." },
      { type: "h3", text: "Key terms" },
      { type: "ul", items: [
        "**Data:** wo examples jinse machine seekhti hai (jaise 10,000 emails).",
        "**Features:** data ki properties jo model dekhta hai (email mein \"free\", \"lottery\" jaise words, sender, links ki ginti).",
        "**Label:** sahi jawab (\"spam\" ya \"not spam\").",
        "**Model:** seekha hua pattern, ek mathematical function jo naye data par prediction deta hai.",
        "**Training:** model ko data dikha kar seekhne ka process.",
        "**Inference:** training ke baad model ko naye data par use karna (prediction lena).",
      ] },
      { type: "h3", text: "ML ke 3 main types" },
      { type: "table", head: ["Type", "Kaise seekhta hai", "Example"], rows: [
        ["Supervised Learning", "Label wale data se (sawal + sahi jawab)", "Spam detection, house price prediction"],
        ["Unsupervised Learning", "Bina label ke, khud groups dhoondta hai", "Customers ko groups mein baantna"],
        ["Reinforcement Learning", "Try karke, reward/penalty se seekhta hai", "Game khelne wala AI, robot walking"],
      ] },
      { type: "tip", label: "Example", text: "Bachche ko 100 photos dikhao jinme \"billi\" aur \"kutta\" likha ho. Kuch time baad wo nayi photo dekh kar bata dega. Ye supervised learning hai." },
    ],
  },
  {
    id: "classical-ml",
    title: "2. Classical ML",
    blocks: [
      { type: "p", text: "Neural networks aur LLM se pehle ke time ke ML algorithms ko \"classical ML\" kehte hain. Aaj bhi table-type data (Excel/database jaisa) par ye sabse reliable aur fast hain." },
      { type: "table", head: ["Algorithm", "Kya karta hai", "Simple example"], rows: [
        ["Linear Regression", "Ek number predict karta hai, seedhi line fit karke", "Ghar ka size dekh kar price predict karna"],
        ["Logistic Regression", "Haan/Na (2 categories) predict karta hai", "Customer loan chukayega ya nahi"],
        ["Decision Tree", "Sawalon ka tree (\"salary > 50k?\" → haan/na)", "Bank loan approval ke rules"],
        ["Random Forest", "Bahut saare trees ka voting, zyada accurate", "Fraud detection"],
        ["K-Means (Clustering)", "Similar cheezon ko groups mein baantta hai", "Customer segmentation"],
        ["SVM", "Do groups ke beech best dividing line dhoondta hai", "Text classification"],
        ["KNN", "Nazdeeki padosiyon ko dekh kar decide karta hai", "Movie recommendation"],
      ] },
      { type: "h3", text: "Do main kaam" },
      { type: "ul", items: [
        "**Regression:** number predict karna (price, temperature, sales).",
        "**Classification:** category predict karna (spam/not spam, cat/dog).",
      ] },
      { type: "tip", label: "Yaad rakho", text: "Har problem ke liye neural network zaroori nahi. Chhote structured data par Random Forest ya XGBoost aksar deep learning ko hara dete hain." },
    ],
  },
  {
    id: "model-evaluation",
    title: "3. Model Evaluation",
    blocks: [
      { type: "p", text: "Model bana liya, ab kaise pata chale ki wo accha hai ya nahi? Isi ko evaluation kehte hain. Golden rule: model ko wahi data dikha kar test mat karo jis par wo train hua hai, warna ye ratta maarne wale student ko wahi sawal poochne jaisa hai." },
      { type: "h3", text: "Data ka split" },
      { type: "ul", items: [
        "**Training set (~70-80%):** isse model seekhta hai.",
        "**Validation set:** training ke beech settings tune karne ke liye.",
        "**Test set (~10-20%):** final exam. Model ne ye kabhi nahi dekha hota.",
      ] },
      { type: "h3", text: "Important metrics (spam detection ke example se)" },
      { type: "table", head: ["Metric", "Matlab", "Kab dekhein"], rows: [
        ["Accuracy", "Kitne prediction sahi nikle (total mein se)", "Jab classes balanced hon"],
        ["Precision", "Jinhe model ne \"spam\" kaha, unme se kitne sach mein spam the", "Jab false alarm mehenga ho"],
        ["Recall", "Sach mein jitne spam the, unme se kitne model ne pakde", "Jab kuch miss karna mehenga ho (jaise cancer detection)"],
        ["F1 Score", "Precision aur Recall ka balance", "Jab dono zaroori hon"],
        ["RMSE / MAE", "Number predict karne mein average galti", "Regression problems"],
      ] },
      { type: "tip", label: "Accuracy ka dhokha", text: "Agar 99% emails normal hain aur model har email ko \"normal\" bol de, toh accuracy 99% hogi, par model bekaar hai. Isliye precision aur recall bhi dekhte hain." },
      { type: "h3", text: "Overfitting vs Underfitting" },
      { type: "ul", items: [
        "**Overfitting:** model ne training data ratt liya. Training mein 99%, naye data par 70%. Student ne sirf purane papers ratte.",
        "**Underfitting:** model ne kuch seekha hi nahi. Dono jagah kharab result.",
        "**Goal:** \"just right\" model jo naye data par bhi achha chale (generalization).",
      ] },
      { type: "p", text: "**Cross-validation:** data ko kai hisson mein baant kar baari-baari test karna, taaki result kisi ek lucky split par depend na kare." },
    ],
  },
  {
    id: "neural-networks",
    title: "4. Neural Networks",
    blocks: [
      { type: "p", text: "Neural network insaani dimaag se inspired hai, par asal mein ye bas math ka ek bada jaal hai. Isme bahut saare chhote \"neurons\" hote hain jo layers mein arrange hote hain." },
      { type: "h3", text: "Basic structure" },
      { type: "ul", items: [
        "**Input layer:** data andar aata hai (jaise image ke pixels).",
        "**Hidden layers:** beech ki layers jo pattern nikaalti hain. Layers jitni zyada, network utna \"deep\" (isiliye *Deep* Learning).",
        "**Output layer:** final jawab (\"ye billi hai, 95%\").",
      ] },
      { type: "h3", text: "Seekhta kaise hai?" },
      { type: "ol", items: [
        "**Weights:** har connection ka ek number hota hai jo batata hai ki wo input kitna important hai. Seekhna matlab in weights ko sahi karna.",
        "**Forward pass:** input layers se guzar kar prediction banata hai.",
        "**Loss:** prediction aur sahi jawab ka fark, yaani galti kitni badi thi.",
        "**Backpropagation:** galti ko peeche ki taraf bhej kar har weight thoda sudhaara jaata hai.",
        "**Repeat:** ye process lakhon baar hota hai, aur galti dheere-dheere kam hoti jaati hai.",
      ] },
      { type: "tip", label: "Analogy", text: "Cricket mein bowler ball fenkta hai, dekhta hai kitna door gira (loss), aur agli baar apna action thoda adjust karta hai (backpropagation). Hazaron baar practice ke baad line-length perfect ho jaati hai." },
      { type: "h3", text: "Popular architectures" },
      { type: "table", head: ["Type", "Kisme best hai", "Example"], rows: [
        ["CNN", "Images", "Face recognition, X-ray analysis"],
        ["RNN / LSTM", "Sequences (purana approach)", "Purane speech aur translation systems"],
        ["Transformer", "Language aur ab images bhi", "ChatGPT, Claude, Gemini (isi par bane hain)"],
      ] },
      { type: "p", text: "Transformer 2017 mein aaya aur isi ne GenAI ki duniya badal di. Aage LLM section mein iska role samjhenge." },
    ],
  },
  {
    id: "llm-fundamentals",
    title: "5. LLM Fundamentals",
    blocks: [
      { type: "p", text: "**LLM (Large Language Model)** ek bahut bada neural network (Transformer) hai jo internet, books aur code ke arabon words par train hua hai. Uska basic kaam hai: *pichhle words dekh kar agla word predict karna.* Bas itna hi, par itne bade scale par ki wo baat-cheet, code aur essay sab likh leta hai." },
      { type: "h3", text: "Important terms" },
      { type: "ul", items: [
        "**Token:** LLM words ko chhote tukdon mein todta hai. \"Namaste\" kai tokens ban sakta hai. Pricing aur limits tokens mein hoti hain.",
        "**Context window:** ek baar mein model kitna text yaad rakh sakta hai (aapki baatein + uska jawab). Ye uski \"short-term memory\" hai.",
        "**Parameters:** model ke andar ke weights. \"70B model\" matlab 70 billion parameters.",
        "**Prompt:** wo instruction ya sawal jo aap model ko dete ho. Prompt jitna clear, jawab utna behtar (isko *prompt engineering* kehte hain).",
        "**Temperature:** randomness ka knob. Kam value par jawab stable, zyada par creative.",
        "**Hallucination:** jab model confidently galat ya banaya hua jawab de deta hai. Wo sach nahi \"jaanta\", sirf likely lagne wala text banata hai.",
      ] },
      { type: "h3", text: "LLM kaise banta hai (3 stages)" },
      { type: "ol", items: [
        "**Pre-training:** internet ke massive data se language seekhna. Mehenga, mahino ka kaam, hazaron GPUs.",
        "**Fine-tuning / Instruction tuning:** specific kaam ya style sikhana, taaki wo sawalon ke theek jawab de.",
        "**RLHF / Alignment:** insaani feedback se model ko helpful aur safe banana.",
      ] },
      { type: "h3", text: "RAG (Retrieval-Augmented Generation)" },
      { type: "p", text: "**Problem:** LLM ko aapki company ke private documents ya latest news ka pata nahi hota, aur wo hallucinate bhi kar sakta hai." },
      { type: "p", text: "**Solution RAG:** jawab dene se pehle model ko *sahi documents dhoondh kar* saath mein de do. Ye open-book exam jaisa hai." },
      { type: "ol", items: [
        "Aapke documents ko chhote tukdon mein todkar **embeddings** (numbers mein meaning) bana kar **vector database** mein rakhte hain.",
        "User sawal poochta hai, system sabse relevant tukde dhoondhta hai (**Retrieval**).",
        "Wo tukde + sawal LLM ko prompt mein diye jaate hain (**Augmented**).",
        "LLM unke basis par jawab banata hai (**Generation**).",
      ] },
      { type: "tip", label: "Fayda", text: "Model dobara train kiye bina latest aur private data use ho jaata hai, aur jawab ka source bhi dikha sakte hain." },
      { type: "tip", label: "Fine-tuning vs RAG", text: "Naya *knowledge* chahiye (documents, FAQs) toh RAG. Model ka *style ya behaviour* badalna ho toh fine-tuning." },
    ],
  },
  {
    id: "model-serving",
    title: "6. Model Serving",
    blocks: [
      { type: "p", text: "Model train ho gaya, par wo sirf ek file hai. **Model serving** ka matlab hai us model ko aise chalana ki asli users aur apps use kar sakein. Jaise recipe likhna ek baat hai, aur restaurant mein roz hazaron customers ko khana serve karna doosri." },
      { type: "h3", text: "Basic flow" },
      { type: "ol", items: [
        "App ya user request bhejta hai (jaise ek sawal).",
        "Request ek **API** (aksar REST ya streaming) se server par pahunchti hai.",
        "Server par model **inference** chalata hai (prediction banata hai).",
        "Jawab wapas user ko jaata hai.",
      ] },
      { type: "h3", text: "Key terms" },
      { type: "ul", items: [
        "**Inference:** trained model se jawab nikalna. Training ek baar, inference baar-baar.",
        "**Latency:** jawab aane mein kitna time laga. LLM mein \"pehla token kitni jaldi aaya\" bhi dekhte hain.",
        "**Throughput:** ek second mein kitni requests handle hui.",
        "**GPU:** bade models CPU par bahut slow chalte hain, isliye GPU chahiye. GPU memory (VRAM) aksar sabse badi limit hoti hai.",
        "**Batching:** kai users ki requests ek saath process karna, taaki GPU ka poora use ho.",
        "**Quantization:** model ko chhote numbers (jaise 16-bit se 4-bit) mein convert karna, taaki kam memory aur sasta chale, thodi accuracy ke badle.",
        "**Scaling:** zyada traffic aaye toh servers badhana (Kubernetes jaise tools se).",
        "**Monitoring:** latency, errors, cost aur jawab ki quality par nazar rakhna.",
      ] },
      { type: "h3", text: "Do tareeke use karne ke" },
      { type: "table", head: ["Tareeka", "Matlab", "Kab sahi"], rows: [
        ["Hosted API", "OpenAI, Anthropic, Google jaisi company ka model API se use karo", "Jaldi start karna ho, infra ki tension nahi"],
        ["Self-hosted", "Open model (Llama, Mistral) apne server par chalao (vLLM, Ollama jaise tools)", "Data bahar nahi bhejna, ya cost aur control chahiye"],
      ] },
      { type: "tip", label: "Yaad rakho", text: "Production mein model ke saath sabse badi dikkat usse *reliably, tez aur sasta* chalana hoti hai, sirf accurate banana nahi." },
    ],
  },
  {
    id: "agents",
    title: "Agent aur Agentic AI",
    blocks: [
      { type: "p", text: "**Chatbot vs Agent:** Chatbot sirf jawab deta hai. **Agent** jawab dene ke saath *kaam bhi karta hai*: tools use karta hai, steps plan karta hai, aur result dekh kar agla step decide karta hai." },
      { type: "tip", label: "Analogy", text: "Chatbot ek aisa dost hai jo flight ke options bata deta hai. Agent wo assistant hai jo options dekhta hai, aapka calendar check karta hai, ticket book karta hai aur confirmation email bhej deta hai." },
      { type: "h3", text: "Agent ke main parts" },
      { type: "ul", items: [
        "**LLM (dimaag):** soch-vichaar aur decisions.",
        "**Tools:** search, calculator, database, email, code run karna, APIs.",
        "**Memory:** pichhli baatein aur kaam ka context yaad rakhna.",
        "**Loop:** sochna → tool chalana → result dekhna → phir sochna, jab tak kaam poora na ho.",
      ] },
      { type: "p", text: "**Agentic AI kya hai?** Ye wo approach hai jisme AI system apne goal ke liye kaafi had tak khud plan banata, tools chalata, galtiyan sudharta aur kai steps mein kaam poora karta hai, insaan ko har step par batane ki zaroorat kam hoti hai. Simple bhasha mein: **Agent ek cheez hai, Agentic AI us tarah ke autonomous kaam karne ka tareeka.**" },
      { type: "h3", text: "Related terms" },
      { type: "ul", items: [
        "**Tool calling / Function calling:** LLM ka bahar ke tools ko chalane ka tareeka.",
        "**MCP (Model Context Protocol):** AI ko tools aur data se jodne ka ek standard, jaise USB-C har device ke liye.",
        "**Multi-agent system:** kai agents milkar kaam karte hain (ek research kare, ek likhe, ek review kare).",
        "**Human-in-the-loop:** important actions (payment, delete) se pehle insaan ki approval.",
      ] },
      { type: "tip", label: "Dhyan rakho", text: "Agents galtiyan bhi kar sakte hain aur asli actions le sakte hain, isliye permissions limited rakhein aur critical kaam mein approval zaroor lein." },
    ],
  },
  {
    id: "cheat-sheet",
    title: "Buzzword Cheat Sheet",
    blocks: [
      { type: "table", head: ["Term", "Ek line mein matlab"], rows: [
        ["AI", "Machine ko smart kaam karwana (sabse bada umbrella)"],
        ["ML", "Examples se seekhne wali machine"],
        ["Deep Learning", "Neural networks wala ML"],
        ["Neural Network", "Layers mein juda neurons ka math model"],
        ["Training", "Model ko data se seekhana"],
        ["Inference", "Trained model se jawab nikalna"],
        ["Overfitting", "Model ne ratt liya, naye data par fail"],
        ["Transformer", "Aaj ke LLMs ka underlying architecture"],
        ["LLM", "Bahut bada language model, agla word predict karta hai"],
        ["Token", "Text ka chhota tukda jise LLM padhta hai"],
        ["Prompt", "Model ko diya gaya instruction"],
        ["Hallucination", "Model ka confident par galat jawab"],
        ["Fine-tuning", "Model ko specific kaam ke liye aur train karna"],
        ["Embedding", "Text ka meaning numbers mein"],
        ["Vector DB", "Embeddings store aur search karne wala database"],
        ["RAG", "Jawab se pehle relevant documents dhoondh kar model ko dena"],
        ["Agent", "LLM + tools + loop, jo kaam karke dikhata hai"],
        ["Agentic AI", "Khud plan karke multi-step kaam karne wala AI approach"],
        ["MCP", "AI ko tools/data se jodne ka standard"],
        ["Model Serving", "Model ko users ke liye production mein chalana"],
        ["Quantization", "Model ko chhota karna, kam memory mein chalane ke liye"],
      ] },
      { type: "tip", label: "Ek-line story (dost ko sunane ke liye)", text: "ML data se pattern seekhta hai, neural networks us seekhne ko bahut powerful banate hain, LLM language ke liye bana bada neural network hai, RAG usko sahi documents dikhata hai, aur Agent usko tools dekar asli kaam karwata hai. Model serving ye sab kuch users tak pahunchati hai." },
    ],
  },
];

function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**")) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part.startsWith("*")) return <em key={i}>{part.slice(1, -1)}</em>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "p":
      return <p className="asm-p"><Inline text={block.text} /></p>;
    case "h3":
      return <h3 className="asm-h3">{block.text}</h3>;
    case "ul":
      return (
        <ul className="asm-list">
          {block.items.map((t, i) => <li key={i}><Inline text={t} /></li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="asm-list">
          {block.items.map((t, i) => <li key={i}><Inline text={t} /></li>)}
        </ol>
      );
    case "table":
      return (
        <div className="asm-table-wrap">
          <table className="asm-table">
            <thead>
              <tr>{block.head.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} data-label={block.head[c]}>
                      {c === 0 ? <strong>{cell}</strong> : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "tip":
      return (
        <div className="asm-tip">
          <span className="asm-tip-label">{block.label}</span>
          <span><Inline text={block.text} /></span>
        </div>
      );
    default:
      return null;
  }
}

export default function AISamjho() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
  };

  return (
    <div className="asm-root">
      <style>{CSS}</style>

      <header className="asm-hero">
        <p className="asm-kicker">Hinglish Guide</p>
        <h1>AI Samjho</h1>
        <p className="asm-sub">
          AI, ML, LLM, RAG, Agent aur Agentic AI jaise buzzwords, bina jargon ke,
          daily-life examples se. Basics se shuru karke LLM, serving aur agents tak.
        </p>
      </header>

      <div className="asm-layout">
        <nav className="asm-nav" aria-label="Sections">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={go(s.id)}
              className={active === s.id ? "is-active" : ""}
            >
              {s.title}
            </a>
          ))}
        </nav>

        <main className="asm-main">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="asm-section">
              <h2 className="asm-h2">{s.title}</h2>
              {s.blocks.map((b, i) => <Block key={i} block={b} />)}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

const CSS = `
.asm-root {
  --bg: #ffffff; --bg-soft: #f5f6f8; --text: #1b1f27; --muted: #5b6472;
  --border: #e2e5ea; --accent: #4f46e5; --accent-soft: #eef0ff; --tip: #fff7e6; --tip-border: #f0c36d;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--text); background: var(--bg); line-height: 1.7;
  width: 100%; padding: 0 1.5rem 64px; box-sizing: border-box;
}
.asm-root *, .asm-root *::before, .asm-root *::after { box-sizing: border-box; }

[data-theme="dark"] .asm-root {
  --bg: #14161b; --bg-soft: #1c1f26; --text: #e8eaee; --muted: #9aa3b2;
  --border: #2b303a; --accent: #8b93ff; --accent-soft: #232650; --tip: #2a2417; --tip-border: #8a6b25;
}
@media (prefers-color-scheme: dark) {
  .asm-root:not([data-theme="light"] .asm-root) {
    --bg: #14161b; --bg-soft: #1c1f26; --text: #e8eaee; --muted: #9aa3b2;
    --border: #2b303a; --accent: #8b93ff; --accent-soft: #232650; --tip: #2a2417; --tip-border: #8a6b25;
  }
}

.asm-hero { padding: 56px 0 32px; border-bottom: 1px solid var(--border); }
.asm-kicker { margin: 0 0 8px; font-size: 13px; letter-spacing: .08em; text-transform: uppercase; color: var(--accent); font-weight: 600; }
.asm-hero h1 { margin: 0 0 12px; font-size: clamp(32px, 6vw, 52px); line-height: 1.1; }
.asm-sub { margin: 0; color: var(--muted); font-size: 18px; }

.asm-layout { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 40px; margin-top: 32px; width: 100%; }
.asm-nav { position: sticky; top: 16px; align-self: start; display: flex; flex-direction: column; gap: 2px; max-height: calc(100vh - 32px); overflow-y: auto; }
.asm-nav a { padding: 8px 12px; border-radius: 8px; color: var(--muted); text-decoration: none; font-size: 14px; line-height: 1.35; border-left: 3px solid transparent; }
.asm-nav a:hover { background: var(--bg-soft); color: var(--text); }
.asm-nav a.is-active { background: var(--accent-soft); color: var(--accent); border-left-color: var(--accent); font-weight: 600; }

.asm-main { min-width: 0; width: 100%; }
.asm-section { padding: 8px 0 28px; scroll-margin-top: 16px; }
.asm-h2 { margin: 24px 0 12px; font-size: 28px; line-height: 1.25; }
.asm-h3 { margin: 22px 0 8px; font-size: 17px; }
.asm-p { margin: 0 0 14px; }
.asm-list { margin: 0 0 14px; padding-left: 22px; }
.asm-list li { margin-bottom: 6px; }

.asm-tip { display: flex; flex-direction: column; gap: 2px; margin: 16px 0; padding: 14px 16px; background: var(--tip); border-left: 4px solid var(--tip-border); border-radius: 8px; }
.asm-tip-label { font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: .04em; }

.asm-table-wrap { overflow-x: auto; margin: 12px 0 18px; border: 1px solid var(--border); border-radius: 10px; width: 100%; }
.asm-table { width: 100%; border-collapse: collapse; font-size: 15px; }
.asm-table th { text-align: left; background: var(--bg-soft); font-weight: 600; }
.asm-table th, .asm-table td { padding: 10px 14px; border-bottom: 1px solid var(--border); vertical-align: top; }
.asm-table tbody tr:last-child td { border-bottom: 0; }

@media (max-width: 860px) {
  .asm-layout { grid-template-columns: 1fr; gap: 16px; }
  .asm-nav { position: sticky; top: 0; z-index: 5; flex-direction: row; overflow-x: auto; max-height: none; padding: 8px 0; background: var(--bg); border-bottom: 1px solid var(--border); }
  .asm-nav a { white-space: nowrap; border-left: 0; border-bottom: 3px solid transparent; border-radius: 6px 6px 0 0; }
  .asm-nav a.is-active { border-bottom-color: var(--accent); }
  .asm-h2 { font-size: 24px; }
  .asm-section { scroll-margin-top: 64px; }
}
`;
